import { useState, useRef, useEffect, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import api from '../services/api';
import { I18N_ROUTING_ENABLED, LANG_COOKIE_NAME, type AppLocale } from '../i18n';
import { localePrefix, localizedPath, routeIdFromSlug, translatePath } from '../i18n/slugMap';
import { ALL_POSTS } from '../content/blog';

/**
 * Troca de idioma (Release C). Duas variantes responsivas:
 *  - Desktop (≥ sm): dropdown compacto (bandeira + chevron).
 *  - Mobile (< sm): 3 bandeiras inline lado a lado, com destaque na escolhida.
 *
 * Mobile-first; visíveis via Tailwind responsive (sm:hidden / hidden sm:block).
 * O App mobile (apps/mobile) já segue o padrão de 3 bandeiras inline.
 *
 * SVG inline (não emoji) porque flag emoji renderiza inconsistente no Windows.
 * Detecção automática do idioma: i18next-browser-languagedetector (ordem
 * path → localStorage → navigator), configurada em `i18n/index.ts`.
 */
const LOCALES: { code: AppLocale; label: string; Flag: () => ReactElement }[] = [
  { code: 'pt-BR', label: 'Português', Flag: FlagBR },
  { code: 'en', label: 'English', Flag: FlagUS },
  { code: 'es', label: 'Español', Flag: FlagES },
];

export function LangSwitcher() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = (i18n.language || 'pt-BR') as string;

  // Fecha o dropdown ao clicar fora. Listener no document (não overlay fixed):
  // o PublicHeader tem backdrop-blur, que quebra `position: fixed` em descendentes.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Resolve o locale ativo — tolera variações (ex: 'en-US' → 'en').
  const active =
    LOCALES.find((l) => current === l.code) ??
    LOCALES.find((l) => current.startsWith(l.code.split('-')[0])) ??
    LOCALES[0];
  const ActiveFlag = active.Flag;

  const change = (code: AppLocale) => {
    setOpen(false);
    if (active.code === code) return;
    i18n.changeLanguage(code);
    try {
      localStorage.setItem(LANG_COOKIE_NAME, code);
    } catch {
      /* storage indisponível — segue só em memória */
    }
    // Persiste no backend se logado (não-bloqueante).
    if (localStorage.getItem('token')) {
      api.put('/i18n/me/locale', { locale: code }).catch(() => {});
    }

    // Invalida o cache do React Query pra que conteúdo localizado pelo
    // backend (achievements, banners, competition announcements, etc.) seja
    // re-fetched no novo idioma. Sem isso, os dados ficavam congelados no
    // idioma da primeira carga (bug reportado em DEV 2026-05-25: trocava
    // pra ES e as 10 conquistas continuavam em EN).
    queryClient.invalidateQueries();

    // i18n routing: navegar pro slug do novo idioma quando estiver numa
    // landing pública conhecida. Sem isso, o user trocava pra EN mas
    // continuava em /bolao-brasileirao (e o CloudFront serve o HTML PT
    // estático, fora de sync com o i18n.language atual).
    if (!I18N_ROUTING_ENABLED) return;

    const oldPath = location.pathname;
    // Strip prefix /en ou /es se houver, pra obter o slug "raw"
    const m = oldPath.match(/^\/(en|es)(\/.*|$)/);
    const sourceLocale: AppLocale = m ? (m[1] as AppLocale) : 'pt-BR';
    const rest = m ? m[2] : oldPath;
    const slug = rest === '' || rest === '/' ? '' : rest.replace(/^\//, '').split('/')[0];

    // Caso home (slug vazio) — só ajusta o prefix de idioma.
    if (slug === '') {
      const target = localePrefix(code) || '/';
      if (target !== oldPath) navigate(target + location.search + location.hash);
      return;
    }

    // Resolve routeId a partir do slug atual no idioma de origem.
    // - Landings (single-segment): traduz via routeIdFromSlug+localizedPath.
    // - Rotas autenticadas multi-segmento (/pools/:id/matches): translatePath
    //   reescreve preservando IDs no meio e traduzindo sub-slugs.
    const routeId = routeIdFromSlug(slug, sourceLocale);
    if (routeId) {
      // Preserva subpaths além do slug (raro nas landings, mas defensivo)
      const subpath = rest.slice(1 + slug.length);

      // Caso especial: rota de blog post. O subpath é o slug do post, que tem
      // tradução por idioma no `i18n[locale].slug` (ver `lib/blog.ts`). Sem
      // este branch, o user troca pra EN mas a URL fica com slug PT — o
      // conteúdo até é traduzido (BlogPost.tsx faz fallback), só que o
      // navegador continua mostrando a URL antiga (visual + SEO degrade).
      if (routeId === 'blog' && subpath.startsWith('/')) {
        const postSlug = subpath.slice(1).split('/')[0];
        if (postSlug) {
          // Resolve post raw via ALL_POSTS — precisa do BlogPost (não do
          // ResolvedBlogPost) pra ter acesso ao mapa `i18n` por idioma.
          // Match em qualquer locale (user pode estar em /en/blog/{slugPt}
          // se veio de link antigo — devemos achar o post mesmo assim).
          const post = ALL_POSTS.find((p) =>
            (Object.keys(p.i18n) as AppLocale[]).some((l) => p.i18n[l]?.slug === postSlug),
          );
          const newPostSlug = post?.i18n[code]?.slug ?? postSlug;
          const target = localizedPath('blog', code) + '/' + newPostSlug + location.search + location.hash;
          if (target !== oldPath + location.search + location.hash) navigate(target);
          return;
        }
      }

      // Multi-segmento dinâmico (pools-base, competicoes-base, admin-base):
      // o subpath inclui IDs e sub-slugs (ex: /pools/123/matches). Concat
      // simples deixaria 'matches' em PT mesmo trocando pra EN — a URL
      // resultante /en/pools/123/matches não está registrada no React
      // Router (que espera /en/pools/123/predictions). Cai no catch-all →
      // Navigate('/') → no app subdomain, CloudFront 301 pro apex (bug
      // reportado em DEV 2026-06-02 em /pools/{id}/rankings e similares).
      // Solução: delegar pro translatePath que traduz cada segmento.
      if (routeId.endsWith('-base') && subpath.startsWith('/')) {
        const translated = translatePath(oldPath, code);
        if (translated) {
          const target = translated + location.search + location.hash;
          if (target !== oldPath + location.search + location.hash) navigate(target);
          return;
        }
      }

      const target = localizedPath(routeId, code) + subpath + location.search + location.hash;
      if (target !== oldPath + location.search + location.hash) navigate(target);
      return;
    }

    // Fallback: multi-segmento (pools-base, competicoes-base, admin-base etc).
    // Normaliza pra path PT-BR primeiro (caso user esteja em /en|/es), depois
    // traduz pro locale alvo. Preserva IDs numéricos dinâmicos no meio.
    const ptCanonical = sourceLocale === 'pt-BR'
      ? oldPath
      : (translatePath(oldPath, 'pt-BR') ?? oldPath);
    const translated = translatePath(ptCanonical, code);
    if (!translated) return;
    const target = translated + location.search + location.hash;
    if (target !== oldPath + location.search + location.hash) navigate(target);
  };

  return (
    <>
      {/* ── Mobile (< sm): 3 bandeiras inline com destaque na escolhida ── */}
      <div
        role="radiogroup"
        aria-label={t('lang.list', 'Idiomas')}
        className="inline-flex sm:hidden items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5"
      >
        {LOCALES.map(({ code, label, Flag }) => {
          const isActive = active.code === code;
          return (
            <button
              key={code}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-label={t('lang.change_to', { name: label, defaultValue: 'Mudar para {{name}}' })}
              onClick={() => change(code)}
              className={`flex items-center justify-center rounded px-2 py-1 transition-all ${
                isActive
                  ? 'bg-primary-100 ring-1 ring-inset ring-primary-300 scale-105'
                  : 'opacity-50 hover:opacity-100 hover:bg-gray-50'
              }`}
            >
              <Flag />
            </button>
          );
        })}
      </div>

      {/* ── Desktop (≥ sm): dropdown compacto ────────────────────────────── */}
      <div className="relative hidden sm:block" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={t('lang.current_change', {
            name: active.label,
            defaultValue: 'Idioma: {{name}}. Trocar idioma',
          })}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <ActiveFlag />
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <ul
            role="listbox"
            aria-label={t('lang.list', 'Idiomas')}
            className="absolute right-0 mt-2 z-50 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden py-1"
          >
            {LOCALES.map(({ code, label, Flag }) => {
              const isActive = active.code === code;
              return (
                <li key={code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    aria-label={t('lang.change_to', { name: label, defaultValue: 'Mudar para {{name}}' })}
                    onClick={() => change(code)}
                    className={`flex items-center justify-center px-3 py-2 transition-colors ${
                      isActive ? 'bg-primary-50 ring-1 ring-inset ring-primary-200' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Flag />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

/* ── Bandeiras SVG (22x16) ─────────────────────────────────────────────── */

function FlagBR() {
  return (
    <svg width="22" height="16" viewBox="0 0 22 16" aria-hidden="true" className="flex-shrink-0 rounded-sm">
      <rect width="22" height="16" fill="#009b3a" />
      <path d="M11 2 20 8 11 14 2 8Z" fill="#fedf00" />
      <circle cx="11" cy="8" r="3.2" fill="#002776" />
    </svg>
  );
}

function FlagUS() {
  return (
    <svg width="22" height="16" viewBox="0 0 22 16" aria-hidden="true" className="flex-shrink-0 rounded-sm">
      <rect width="22" height="16" fill="#b22234" />
      {[1, 3, 5, 7, 9, 11, 13].map((y) => (
        <rect key={y} y={y} width="22" height="1.23" fill="#fff" />
      ))}
      <rect width="9" height="8.6" fill="#3c3b6e" />
    </svg>
  );
}

function FlagES() {
  return (
    <svg width="22" height="16" viewBox="0 0 22 16" aria-hidden="true" className="flex-shrink-0 rounded-sm">
      <rect width="22" height="16" fill="#c60b1e" />
      <rect y="4" width="22" height="8" fill="#ffc400" />
    </svg>
  );
}
